import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import { 
  Paper, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  CircularProgress,
  Snackbar
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import EditIcon from '@material-ui/icons/Edit';
import { Alert, myTheme, Styles } from '../Tools';
import { getSystemParameters, updateSystemParameter } from '../../api/karthus.client';

export interface SystemParameter {
  key: string;
  value: string;
  description: string;
}

const paramNameLabels: Record<string, string> = {
  'GeofenceToleranceMeters': 'Tolerancia de Geocerca (Metros)'
};

export function SystemParameters() {
  const classes = Styles();
  const [parameters, setParameters] = useState<SystemParameter[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  const [editingParamKey, setEditingParamKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const [snackbarMessage, setSnackbarMessage] = useState<{ open: boolean; message: string; type: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    type: 'success',
  });

  const fetchParameters = async () => {
    setLoading(true);
    try {
      const res = await getSystemParameters();
      if (res.status === 200) {
        setParameters((res.result as any).data || res.result);
      } else {
        setSnackbarMessage({
          open: true,
          message: 'Error al cargar los parámetros del sistema.',
          type: 'error'
        });
      }
    } catch (error) {
      setSnackbarMessage({
        open: true,
        message: 'Error al conectar con el servidor.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParameters();
  }, []);

  const handleEditClick = (param: SystemParameter) => {
    setEditingParamKey(param.key);
    setEditValue(param.value);
  };

  const handleCancel = () => {
    setEditingParamKey(null);
    setEditValue('');
  };

  const handleSave = async (param: SystemParameter) => {
    setSaving(true);
    try {
      const res = await updateSystemParameter(param.key, {
        key: param.key,
        value: editValue,
        description: param.description
      });

      if (res.status === 200 || res.status === 204) {
        setSnackbarMessage({
          open: true,
          message: 'Parámetro guardado exitosamente.',
          type: 'success'
        });
        setEditingParamKey(null);
        fetchParameters(); 
      } else {
        setSnackbarMessage({
          open: true,
          message: 'Error al guardar el parámetro.',
          type: 'error'
        });
      }
    } catch (error) {
      setSnackbarMessage({
        open: true,
        message: 'Error al conectar con el servidor.',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ThemeProvider theme={myTheme}>
      <Grid container spacing={3} style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
        <Grid item xs={12}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Typography variant="h4" component="h1" gutterBottom style={{ color: '#343c89', fontWeight: 'bold' }}>
              Parámetros del Sistema
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Configure los valores globales utilizados en la plataforma.
            </Typography>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress style={{ color: '#343c89' }} />
            </div>
          ) : (
            <Paper style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.08)', borderRadius: '16px', overflow: 'hidden', padding: '16px 0' }}>
              {parameters && parameters.length > 0 ? (
                parameters.map((param, index) => (
                  <div key={param.key}>
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '32px 40px', alignItems: 'center', textAlign: 'center' }}>
                      <Typography variant="h6" style={{ fontWeight: 'bold', color: '#343c89', marginBottom: '8px' }}>
                        {paramNameLabels[param.key] || param.key}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ maxWidth: '600px', marginBottom: '24px', lineHeight: 1.6 }}>
                        {param.description}
                      </Typography>
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
                        {editingParamKey === param.key ? (
                          <TextField
                            variant="outlined"
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            style={{ width: '120px' }}
                            inputProps={{ style: { textAlign: 'center', fontWeight: 'bold', fontSize: '1.2rem' } }}
                          />
                        ) : (
                          <Typography variant="h5" style={{ fontWeight: 700, color: '#222', minWidth: '120px' }}>
                            {param.value}
                          </Typography>
                        )}
                        
                        <div style={{ display: 'flex', gap: '12px' }}>
                          {editingParamKey === param.key ? (
                            <>
                              <Button
                                variant="contained"
                                onClick={() => handleSave(param)}
                                disabled={saving}
                                style={{ backgroundColor: '#343c89', color: '#fff', minWidth: '100px', fontWeight: 'bold' }}
                              >
                                {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
                              </Button>
                              <Button variant="outlined" onClick={handleCancel} disabled={saving} style={{ fontWeight: 'bold' }}>
                                Cancelar
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => handleEditClick(param)}
                              startIcon={<EditIcon />}
                              style={{ fontWeight: 'bold', borderWidth: '2px' }}
                            >
                              Editar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    {index < parameters.length - 1 && <div style={{ height: '1px', backgroundColor: '#f0f0f0', width: '90%', margin: '0 auto' }} />}
                  </div>
                ))
              ) : (
                <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                  <Typography variant="body1" color="textSecondary">
                    No se encontraron parámetros en el sistema.
                  </Typography>
                </div>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
      
      <Snackbar
        open={snackbarMessage.open}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage({ ...snackbarMessage, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <div>
        <Alert
          onClose={() => setSnackbarMessage({ ...snackbarMessage, open: false })}
          severity={snackbarMessage.type}
        >
          {snackbarMessage.message}
        </Alert>
        </div>
      </Snackbar>
    </ThemeProvider>
  );
}
